import { Effect, Schema, Struct } from 'effect';
import { DateTime } from 'luxon';
import { uuidv4 } from '../utils/utils.js';
var IncidentTrigger;
(function (IncidentTrigger) {
    IncidentTrigger["event_booked"] = "event_booked";
    IncidentTrigger["timeslot_booked"] = "timeslot_booked";
    IncidentTrigger["timeslot_cancelled"] = "timeslot_cancelled";
    IncidentTrigger["punchcard_purchased"] = "punchcard_purchased";
    IncidentTrigger["giftcard_purchased"] = "giftcard_purchased";
})(IncidentTrigger || (IncidentTrigger = {}));
var NotificationMethod;
(function (NotificationMethod) {
    NotificationMethod["email"] = "email";
})(NotificationMethod || (NotificationMethod = {}));
/**
 * `sites/{site}/config/incident` as stored. Every field is optional on the way
 * in because documents written before a field existed are still out there; the
 * defaults are the ones `toNotificationSettings` has always applied.
 */
const NotificationSettingsSchema = Schema.Struct({
    uuid: Schema.String.pipe(Schema.withDecodingDefaultType(Effect.sync(() => uuidv4()))),
    enabled: Schema.Boolean.pipe(Schema.withDecodingDefaultType(Effect.succeed(false))),
    method: Schema.Enum(NotificationMethod).pipe(Schema.withDecodingDefaultType(Effect.succeed(NotificationMethod.email))),
    // Email addresses for now
    recipients: Schema.mutable(Schema.Array(Schema.String)).pipe(Schema.withDecodingDefaultType(Effect.sync(() => []))),
    incidentTriggers: Schema.mutable(Schema.Array(Schema.Enum(IncidentTrigger))).pipe(Schema.withDecodingDefaultType(Effect.sync(() => [])))
}).mapFields(Struct.map(Schema.mutableKey));
const SiteNotificationDocSchema = Schema.Struct({
    notifications: Schema.mutable(Schema.Array(NotificationSettingsSchema)).pipe(Schema.withDecodingDefaultType(Effect.sync(() => [])))
}).mapFields(Struct.map(Schema.mutableKey));
const emptyIncident = () => {
    return {
        id: '',
        triggers: [],
        siteId: '',
        siteName: '',
        timestamp: DateTime.now().toISO(),
        data: {},
        processed: false
    };
};
const emptyNotificationSettings = () => {
    return {
        uuid: uuidv4(),
        enabled: true,
        method: NotificationMethod.email,
        recipients: [],
        incidentTriggers: []
    };
};
const emptySiteNotificationDoc = () => {
    return {
        notifications: []
    };
};
const toIncidentDoc = (raw) => {
    return {
        id: raw.id || '',
        triggers: raw.triggers || [],
        siteId: raw.siteId || '',
        siteName: raw.siteName || '',
        timestamp: raw.timestamp || DateTime.now().toISO(),
        data: raw.data || {},
        processed: raw.processed || false,
        processedAt: raw.processedAt
    };
};
const toNotificationSettings = (raw) => {
    return {
        uuid: raw.uuid || uuidv4(),
        enabled: raw.enabled || false,
        method: raw.method || NotificationMethod.email,
        recipients: Array.isArray(raw.recipients) ? raw.recipients : [],
        incidentTriggers: Array.isArray(raw.incidentTriggers)
            ? raw.incidentTriggers
            : []
    };
};
const toSiteNotificationDoc = (raw) => {
    if (!raw)
        return emptySiteNotificationDoc();
    return {
        notifications: Array.isArray(raw.notifications)
            ? raw.notifications.map(toNotificationSettings)
            : []
    };
};
/**
 * A notification stored without a uuid gets a fresh random one on every read,
 * from the converter and the schema alike, so those cannot be compared.
 */
const blankGeneratedUuids = (doc, raw) => ({
    notifications: doc.notifications.map((settings, index) => raw?.notifications?.[index]?.uuid
        ? settings
        : { ...settings, uuid: '' })
});
const SiteNotificationStored = {
    name: 'SiteNotificationDoc',
    schema: SiteNotificationDocSchema,
    legacy: toSiteNotificationDoc,
    authority: 'schema',
    normalize: blankGeneratedUuids
};
const createIncident = (id, triggers, siteId, siteName, data, timestamp) => {
    return {
        id,
        triggers,
        siteId,
        siteName,
        timestamp: timestamp || DateTime.now().toISO(),
        data,
        processed: false
    };
};
const incidentTriggerLabel = (type) => {
    switch (type) {
        case IncidentTrigger.event_booked:
            return 'Arrangement booket';
        case IncidentTrigger.timeslot_booked:
            return 'Utleie booket';
        case IncidentTrigger.timeslot_cancelled:
            return 'Utleie kansellert';
        case IncidentTrigger.punchcard_purchased:
            return 'Periodekort kjøpt';
        case IncidentTrigger.giftcard_purchased:
            return 'Gavekort kjøpt';
        default:
            return 'Ukjent hendelse';
    }
};
export { createIncident, emptyIncident, emptyNotificationSettings, emptySiteNotificationDoc, incidentTriggerLabel, IncidentTrigger, NotificationMethod, NotificationSettingsSchema, SiteNotificationDocSchema, SiteNotificationStored, toIncidentDoc, toNotificationSettings, toSiteNotificationDoc };
//# sourceMappingURL=incident.js.map